import { useState } from "react";
import { CalendarPlus, Download, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";

const AddDeadline = ({ workspace, item, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [due, setDue] = useState(item.suggested?.date || "");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspace.id}/deadlines`, { title: item.suggested?.label || item.title, due_date: due, confirmed: true, compliance_key: item.key,
        checked_against_source: true, notes: `Checked against: ${item.sources.map((s) => s.url).join(" ")}`.slice(0, 2000) });
      toast.success("Deadline saved. Reminders follow your notification settings."); setOpen(false); onSaved();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={workspace.is_sample} data-testid={`compliance-add-deadline-${item.key}`}><CalendarPlus className="mr-1.5 h-4 w-4" /> Check and add as deadline</Button>;
  return (
    <div className="space-y-2 rounded-md border bg-slate-50 p-3" data-testid={`compliance-deadline-form-${item.key}`}>
      <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-10 bg-white" data-testid={`compliance-deadline-date-${item.key}`} />
      <label className="flex items-start gap-2 text-xs text-slate-700"><Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} className="mt-0.5" data-testid={`compliance-deadline-checked-${item.key}`} />
        I checked this date against the official source and my own records, and confirm it is correct.</label>
      <div className="flex gap-2"><Button size="sm" onClick={save} disabled={!checked || !due || busy} className="bg-navy hover:bg-navy-2" data-testid={`compliance-deadline-save-${item.key}`}>{busy ? "Saving..." : "Save deadline"}</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button></div>
    </div>
  );
};

const AskAi = ({ workspace, item }) => {
  const [q, setQ] = useState("");
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/workspaces/${workspace.id}/compliance/ask`, { item_key: item.key, question: q }); setA(data.answer); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2"><Input value={q} maxLength={500} onChange={(e) => setQ(e.target.value)} placeholder="Ask about this item" className="h-10" data-testid={`compliance-ask-input-${item.key}`} />
        <Button size="sm" onClick={ask} disabled={busy || q.trim().length < 3 || workspace.is_sample} className="h-10 bg-navy hover:bg-navy-2" data-testid={`compliance-ask-button-${item.key}`}><Sparkles className="mr-1 h-4 w-4 text-gold" />{busy ? "..." : "Ask"}</Button></div>
      {a && <p className="whitespace-pre-wrap rounded-md border-l-2 border-gold bg-gold-light/30 p-3 text-sm text-slate-700" data-testid={`compliance-ask-answer-${item.key}`}>{a}<span className="mt-2 block text-[11px] text-slate-500">AI explanation limited to this item. Check the official sources.</span></p>}
    </div>
  );
};

export const ComplianceItem = ({ workspace, item, onChanged }) => {
  const s = item.suggested;
  const base = process.env.REACT_APP_BACKEND_URL;
  return (
    <li data-testid={`compliance-item-${item.key}`} className="space-y-3 rounded-xl border bg-white p-5">
      <h3 className="font-display text-lg font-semibold text-navy">{item.title}</h3>
      <p className="text-sm text-slate-700">{item.summary}</p>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-600">{item.steps.map((t) => <li key={t}>{t}</li>)}</ol>
      {s && <p className="text-sm" data-testid={`compliance-suggested-${item.key}`}><span className="mr-2 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-amber-900">Unconfirmed</span>
        Suggested from your profile: <strong className="text-navy">{formatDate(s.date)}</strong>{s.window_start && ` (window opens ${formatDate(s.window_start)})`}</p>}
      {item.deadline_id ? <p className="text-sm font-medium text-emerald-700" data-testid={`compliance-saved-${item.key}`}>Saved as a confirmed deadline</p>
        : (s || ["franchise_tax"].includes(item.key)) && <AddDeadline workspace={workspace} item={item} onSaved={onChanged} />}
      {item.templates.length > 0 && <div className="flex flex-wrap gap-2">{item.templates.map((t) => (
        <a key={t} href={`${base}/api/workspaces/${workspace.id}/compliance/templates/${t}`} data-testid={`compliance-template-${t}`} className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm text-navy hover:bg-navy/5"><Download className="h-4 w-4" /> {t === "minutes" ? "Meeting minutes template" : "Written consent template"}</a>
      ))}</div>}
      <ul className="space-y-1 border-t pt-3 text-xs" data-testid={`compliance-sources-${item.key}`}>{item.sources.map((src) => (
        <li key={src.key}><a href={src.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-navy underline">{src.title} <ExternalLink className="h-3 w-3" /></a>
          <span className="ml-2 text-slate-500">{src.status === "read" ? `Read by EVR on ${formatDate(src.checked_on)}` : "Official link. Check the text yourself"}</span></li>
      ))}</ul>
      <AskAi workspace={workspace} item={item} />
    </li>
  );
};
